import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatWhen, salesCrmApi, STATUS_LABELS } from "./api";
import "./salescrm.css";

const BUCKETS = [
  { id: "today", label: "Today's leads" },
  { id: "hot", label: "Hot" },
  { id: "follow_ups", label: "Follow-ups" },
  { id: "overdue_follow_ups", label: "Overdue" },
  { id: "backlog", label: "Backlog" },
  { id: "interested", label: "Interested" },
  { id: "open", label: "All open" },
  { id: "bought", label: "Bought" },
  { id: "not_interested", label: "Not interested" },
  { id: "all", label: "All" },
];

const SalesLeadsList = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const bucket = params.get("bucket") || "today";
  const [search, setSearch] = useState(params.get("search") || "");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [scope, setScope] = useState(params.get("scope") || "mine");
  const [employeeId, setEmployeeId] = useState(params.get("employee_id") || "");

  const title = useMemo(
    () => BUCKETS.find((b) => b.id === bucket)?.label || "Leads",
    [bucket]
  );

  const selectedEmployee = employees.find(
    (e) => String(e.id) === String(employeeId)
  );

  const load = async (nextPage = 1, meData = me) => {
    setLoading(true);
    setError(null);
    try {
      let currentMe = meData;
      if (!currentMe) {
        const meRes = await salesCrmApi.me();
        currentMe = meRes.data;
        setMe(currentMe);
        if (currentMe.is_manager) {
          const empRes = await salesCrmApi.employees({ active: "1" });
          setEmployees(empRes.data || []);
          if (!params.get("scope") && !params.get("employee_id")) {
            setScope("all");
          }
        }
      }

      const listParams = {
        bucket,
        search: search.trim() || undefined,
        page: nextPage,
        page_size: 25,
      };
      if (currentMe?.is_manager) {
        if (employeeId) {
          listParams.employee_id = employeeId;
          listParams.scope = "all";
        } else {
          listParams.scope = scope === "mine" ? "mine" : "all";
        }
      } else {
        listParams.scope = "mine";
      }

      const res = await salesCrmApi.listOpportunities(listParams);
      setRows(res.data.results || []);
      setCount(res.data.count || 0);
      setPage(nextPage);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load leads.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, scope, employeeId]);

  const syncParams = (next = {}) => {
    const q = {
      bucket,
      search: search.trim(),
      scope: next.scope ?? scope,
      employee_id: next.employee_id ?? employeeId,
    };
    if (!q.search) delete q.search;
    if (!q.employee_id) delete q.employee_id;
    setParams(q);
  };

  const onSearch = (e) => {
    e.preventDefault();
    syncParams();
    load(1);
  };

  return (
    <div className="child-sidenav">
      <div className="childnav-content scrm-page">
        <div className="scrm-header">
          <div>
            <h2>{title}</h2>
            <p>
              {count} opportunities
              {selectedEmployee
                ? ` · assigned to ${selectedEmployee.name}`
                : scope === "all" && me?.is_manager
                  ? " · all staff"
                  : ""}
              . Fresh leads, backlog and follow-ups are separate queues.
            </p>
          </div>
          <div className="scrm-actions">
            <button
              type="button"
              className="scrm-btn scrm-btn-outline"
              onClick={() => navigate("/dashboard/sales-crm")}
            >
              Dashboard
            </button>
            <button
              type="button"
              className="scrm-btn scrm-btn-primary"
              onClick={() => navigate("/dashboard/sales-crm")}
            >
              Get Today's Leads
            </button>
          </div>
        </div>

        <div className="scrm-toolbar">
          {BUCKETS.map((b) => (
            <button
              key={b.id}
              type="button"
              className={`scrm-btn ${
                bucket === b.id ? "scrm-btn-primary" : "scrm-btn-ghost"
              }`}
              onClick={() => {
                const q = {
                  bucket: b.id,
                  search: search.trim(),
                  scope,
                };
                if (employeeId) q.employee_id = employeeId;
                if (!q.search) delete q.search;
                setParams(q);
              }}
            >
              {b.label}
            </button>
          ))}
        </div>

        <form className="scrm-toolbar" onSubmit={onSearch}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / ICC ID / mobile"
          />
          {me?.is_manager && (
            <>
              <select
                value={employeeId}
                onChange={(e) => {
                  const val = e.target.value;
                  setEmployeeId(val);
                  const nextScope = val ? "all" : scope;
                  if (val) setScope("all");
                  syncParams({ employee_id: val, scope: nextScope });
                }}
                style={{ minWidth: 200 }}
              >
                <option value="">All staff</option>
                {employees.map((e) => (
                  <option key={e.id} value={String(e.id)}>
                    {e.name}
                    {e.role === "manager" ? " (manager)" : ""}
                  </option>
                ))}
              </select>
              {!employeeId && (
                <select
                  value={scope}
                  onChange={(e) => {
                    setScope(e.target.value);
                    syncParams({ scope: e.target.value, employee_id: "" });
                  }}
                >
                  <option value="all">All team leads</option>
                  <option value="mine">My leads only</option>
                </select>
              )}
            </>
          )}
          <button type="submit" className="scrm-btn scrm-btn-outline">
            Search
          </button>
        </form>

        {error && <div className="scrm-alert err">{error}</div>}

        <div className="scrm-table-wrap">
          {loading ? (
            <div className="scrm-empty">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="scrm-empty">
              No leads in this queue. Use <b>Get Today's Leads</b> on the
              dashboard{me?.is_manager ? " (pick a staff member first)" : ""}.
            </div>
          ) : (
            <table className="scrm-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Assigned to</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Follow-up</th>
                  <th>Calls</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="scrm-customer">
                        {row.profile_pic ? (
                          <img
                            className="scrm-avatar"
                            src={row.profile_pic}
                            alt=""
                          />
                        ) : (
                          <div className="scrm-avatar placeholder">
                            {(row.customer_name || "?").charAt(0)}
                          </div>
                        )}
                        <div>
                          <button
                            type="button"
                            className="scrm-linkish"
                            onClick={() =>
                              navigate(`/dashboard/sales-crm/leads/${row.id}`)
                            }
                          >
                            {row.customer_name}
                          </button>
                          <div className="scrm-muted">
                            {row.hcc_id} · {row.mobile_number || "—"}
                          </div>
                          {row.crafts?.length ? (
                            <div className="scrm-muted">
                              {row.crafts.slice(0, 3).join(", ")}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td>{row.assigned_to_name || "—"}</td>
                    <td>
                      <div>{row.source_name || row.source_code || "—"}</div>
                      {row.is_backlog && (
                        <span className="scrm-pill">Backlog</span>
                      )}
                      {row.is_fresh_allocation && !row.is_backlog && (
                        <span className="scrm-muted">Fresh</span>
                      )}
                    </td>
                    <td>
                      <span className={`scrm-pill ${row.status}`}>
                        {STATUS_LABELS[row.status] || row.status}
                      </span>
                    </td>
                    <td>
                      {row.priority === "hot" ? (
                        <span className="scrm-pill hot">Hot</span>
                      ) : (
                        <span className="scrm-muted">Normal</span>
                      )}
                    </td>
                    <td>{formatWhen(row.next_follow_up_at)}</td>
                    <td>
                      {row.connected_calls}/{row.call_attempts}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="scrm-btn scrm-btn-outline"
                        onClick={() =>
                          navigate(`/dashboard/sales-crm/leads/${row.id}`)
                        }
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {count > 25 && (
          <div className="scrm-actions" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="scrm-btn scrm-btn-ghost"
              disabled={page <= 1}
              onClick={() => load(page - 1)}
            >
              Previous
            </button>
            <span className="scrm-muted">
              Page {page} · {count} total
            </span>
            <button
              type="button"
              className="scrm-btn scrm-btn-ghost"
              disabled={page * 25 >= count}
              onClick={() => load(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export { SalesLeadsList };
